import { EventWithDetail } from "@garther/interfaces";
import { AppSidebar } from "@garther/ui/components";
import { cn } from "@garther/ui/libs";
import { Link, useLocation, useMatchRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Calendar,
  ChevronLeft,
  HelpCircle,
  LayoutDashboard,
  MapPin,
  Users,
  WalletMinimal
} from "lucide-react";

import { Logo } from "@/shared/components/logo";

const ORG_NAV_ITEMS = [
  { to: "/org/$orgId", label: "Tổng quan", icon: LayoutDashboard, exact: true },
  { to: "/org/$orgId/event", label: "Sự kiện", icon: Calendar, exact: false },
  {
    to: "/org/$orgId/locations",
    label: "Địa điểm",
    icon: MapPin,
    exact: false
  },
  {
    to: "/org/$orgId/referrals",
    label: "Doanh thu",
    icon: WalletMinimal,
    exact: false
  },
  // {
  //   to: "/org/$orgId/referrals",
  //   label: "Giới thiệu",
  //   icon: Share2,
  //   exact: false
  // },
  { to: "/org/$orgId/members", label: "Thành viên", icon: Users, exact: false }
] as const;

const EVENT_NAV_ITEMS = [
  {
    to: "/org/$orgId/event/$eventId",
    label: "Tổng quan",
    icon: LayoutDashboard,
    exact: true
  },
  {
    to: "/org/$orgId/event/$eventId/sessions",
    label: "Phiên",
    icon: Calendar,
    exact: false
  },
  {
    to: "/org/$orgId/event/$eventId/attendees",
    label: "Người tham dự",
    icon: Users,
    exact: false
  },
  {
    to: "/org/$orgId/event/$eventId/questions",
    label: "Câu hỏi",
    icon: HelpCircle,
    exact: false
  }
] as const;

interface SidebarProps {
  orgId?: string;
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ orgId, open, onClose }: SidebarProps) {
  const { pathname: currentPath } = useLocation();
  const match = useMatchRoute();
  const routerState = useRouterState();
  const navigate = useNavigate();

  const eventMatch = currentPath.match(/\/org\/[^/]+\/event\/([^/]+)/);
  const eventId = eventMatch && eventMatch[1] !== "create" ? (eventMatch[1] ?? null) : null;

  const eventContext = routerState.matches.find(
    (m): m is typeof m & { context: { event: EventWithDetail } } =>
      !!m.context && typeof m.context === "object" && "event" in m.context
  )?.context;
  const currentEvent = eventContext?.event ?? null;

  if (!orgId) return null;

  return (
    <AppSidebar
      open={open}
      onClose={onClose}
      collapseBelow="md"
      className="w-56 md:w-14 lg:w-56 border border-white bg-white/95 md:bg-white/80 rounded-r-md md:rounded-xl shadow-sm"
    >
      <div
        className="py-4 border-b border-border-light flex justify-center gap-2 items-center cursor-pointer"
        onClick={() => navigate({ to: "/" })}
      >
        <Logo className="w-12 h-12 md:w-8 md:h-8 lg:w-12 lg:h-12 md:rounded-full lg:rounded-none" />
        <div className="md:hidden lg:block">
          <p className="text-responsive-sm font-normal text-text-secondary tracking-[0.1em]">TENANT PORTAL</p>
          <p className="text-responsive-2xl font-medium text-text-primary font-literata">GARTHER</p>
        </div>
      </div>
      {eventId !== null ? (
        <>
          <div className="border-b border-border-light px-2 py-1">
            <Link
              to="/org/$orgId/event"
              params={{ orgId }}
              onClick={onClose}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded text-responsive-md text-text-primary font-light",
                "hover:bg-background-highlight md:justify-center lg:justify-start"
              )}
            >
              <ChevronLeft className="w-5 h-5 flex-shrink-0" />
              <p className="flex-1 truncate text-center md:hidden lg:block font-literata">
                {currentEvent?.title ?? "Sự kiện"}
              </p>
              <ChevronLeft className="w-5 h-5 opacity-0 md:hidden lg:block" />
            </Link>
          </div>
          <nav className="flex-1 py-4 space-y-1 px-2">
            {EVENT_NAV_ITEMS.map((item) => {
              const isActive = match({ to: item.to });
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  params={{ orgId, eventId }}
                  onClick={onClose}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded text-responsive-base transition-colors",
                    "md:justify-center lg:justify-start",
                    isActive
                      ? "bg-background-highlight text-text-primary font-medium border border-border-dark font-literata uppercase font-light"
                      : "text-slate-400 hover:bg-background-highlight"
                  )}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="md:hidden lg:inline">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </>
      ) : (
        <nav className="flex-1 py-4 space-y-1 px-2">
          {ORG_NAV_ITEMS.map((item) => {
            const isActive = match({ to: item.to });
            return (
              <Link
                key={item.to}
                to={item.to}
                params={{ orgId }}
                onClick={onClose}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded text-responsive-base transition-colors",
                  "md:justify-center lg:justify-start",
                  isActive
                    ? "bg-background-highlight text-text-primary font-medium border border-border-dark font-literata uppercase font-light"
                    : "text-slate-400 hover:bg-background-highlight"
                )}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span className="md:hidden lg:inline">{item.label}</span>
              </Link>
            );
          })}
        </nav>
      )}
    </AppSidebar>
  );
}
